import PropTypes from 'prop-types'
import classNames from 'classnames'
import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { getBoundGap, isPresent, limit, noop } from 'utils'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'

const getTransformXY = element => {
  const [, x, y] = element.style.transform.match(
    /translate3d\(([ -.0-9]*)[a-zA-Z]*, ([ -.0-9]*)[a-zA-Z]*px/
  ) || [0, 0, 0]
  return { x: +x, y: +y }
}

const draggableContext = createContext({ boundaryElement: null })

const Boundary = forwardRef(({ children, ...attrs }, outerRef) => {
  const innerRef = useRef()
  const ref = outerRef || innerRef
  const [boundaryElement, setBoundaryElement] = useState(null)

  useEffect(() => {
    setBoundaryElement(ref.current)
  }, [ref])

  return (
    <draggableContext.Provider value={{ boundaryElement }}>
      {cloneElement(Children.only(children), { ref, ...attrs })}
    </draggableContext.Provider>
  )
})

Boundary.displayName = 'Draggable.Boundary'
Boundary.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.node,
}

const Draggable = forwardRef(
  (
    {
      onDragStart = noop,
      onDragEnd = noop,
      style,
      zIndexOnDrag,
      resetOnDragEnd,
      className = '',
      classNameOnDrag = '',
      children,
      ...extraProps
    },
    outerRef
  ) => {
    const { boundaryElement } = useContext(draggableContext)
    const innerRef = useRef()
    const ref = outerRef || innerRef
    const [isDragging, setIsDragging] = useState()
    const [cursorStartX, setCursorStartX] = useState(null)
    const [cursorStartY, setCursorStartY] = useState(null)
    const [positionStartX, setPositionStartX] = useState(null)
    const [positionStartY, setPositionStartY] = useState(null)
    const [offsetX, setOffsetX] = useState(0)
    const [offsetY, setOffsetY] = useState(0)
    const [zIndex, setZIndex] = useState(undefined)

    const cursorStartXRef = useUpdatingRef(cursorStartX)
    const cursorStartYRef = useUpdatingRef(cursorStartY)
    const offsetXRef = useUpdatingRef(offsetX)
    const offsetYRef = useUpdatingRef(offsetY)
    const onDragStartRef = useUpdatingRef(onDragStart)
    const onDragEndRef = useUpdatingRef(onDragEnd)

    const handleDragStart = useCallback(
      e => {
        e.stopPropagation()
        e.preventDefault()
        const { x, y } = getTransformXY(ref.current)
        setPositionStartX(x)
        setPositionStartY(y)
        setCursorStartX(e.pageX)
        setCursorStartY(e.pageY)
        setIsDragging(true)
        onDragStartRef.current()
        if (isPresent(zIndexOnDrag)) setZIndex(zIndexOnDrag)
      },
      [ref, onDragStartRef, zIndexOnDrag]
    )

    const handleDragChange = useCallback(
      e => {
        const {
          minX: minVx,
          maxX: maxVx,
          minY: minVy,
          maxY: maxVy,
        } = getBoundGap(boundaryElement, ref.current)
        setOffsetX(_offsetX =>
          limit(
            e.pageX - cursorStartXRef.current,
            _offsetX + minVx,
            _offsetX + maxVx
          )
        )
        setOffsetY(_offsetY =>
          limit(
            e.pageY - cursorStartYRef.current,
            _offsetY + minVy,
            _offsetY + maxVy
          )
        )
      },
      [cursorStartXRef, cursorStartYRef, boundaryElement, ref]
    )

    const handleDragEnd = useCallback(() => {
      setCursorStartX(null)
      setCursorStartY(null)
      setPositionStartX(_positionStartX =>
        resetOnDragEnd ? 0 : _positionStartX + offsetXRef.current
      )
      setPositionStartY(_positionStartY =>
        resetOnDragEnd ? 0 : _positionStartY + offsetYRef.current
      )
      setOffsetX(0)
      setOffsetY(0)
      setIsDragging(false)
      onDragEndRef.current()
      setZIndex(undefined)
    }, [onDragEndRef, resetOnDragEnd, offsetXRef, offsetYRef])

    useEffect(() => {
      if (isDragging) {
        window.document.addEventListener('mouseup', handleDragEnd)
        window.document.addEventListener('mousemove', handleDragChange)
      } else {
        window.document.removeEventListener('mouseup', handleDragEnd)
        window.document.removeEventListener('mousemove', handleDragChange)
      }

      return () => {
        window.document.removeEventListener('mouseup', handleDragEnd)
        window.document.removeEventListener('mousemove', handleDragChange)
      }
    }, [isDragging, handleDragEnd, handleDragChange])

    const _style = {
      transform: `translate3d(${positionStartX + offsetX}px, ${
        positionStartY + offsetY
      }px, 0)`,
      ...(isPresent(zIndexOnDrag) ? { zIndex } : {}),
      ...style,
    }

    return cloneElement(Children.only(children), {
      ref,
      onMouseDown: handleDragStart,
      style: _style,
      className: classNames(Children.only(children).props.className, {
        [classNameOnDrag]: isDragging,
      }),
      ...extraProps,
    })
  }
)

Draggable.displayName = 'Draggable'

export default Draggable

Draggable.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  classNameOnDrag: PropTypes.string,
  onDragEnd: PropTypes.func,
  onDragStart: PropTypes.func,
  resetOnDragEnd: PropTypes.bool,
  style: PropTypes.object,
  tag: PropTypes.string,
  zIndexOnDrag: PropTypes.number,
}

Draggable.Boundary = Boundary
